import OrderedList from '@tiptap/extension-ordered-list'
import { mergeAttributes, type ChainedCommands, type Editor, type SingleCommands } from '@tiptap/core'

export const ALLOWED_ORDERED_LIST_TYPES = ['a', 'A', 'i', 'I'] as const
export type OrderedListMarkerType = (typeof ALLOWED_ORDERED_LIST_TYPES)[number]

const DEFAULT_ORDERED_LIST_TYPE = '1'

function parseOrderedListType(raw: string | null): string | null {
  if (!raw || raw === DEFAULT_ORDERED_LIST_TYPE) {
    return null
  }
  return (ALLOWED_ORDERED_LIST_TYPES as readonly string[]).includes(raw) ? raw : null
}

function orderedListTypeForRender(type: string | null | undefined): Record<string, string> {
  if (!type || type === DEFAULT_ORDERED_LIST_TYPE) {
    return {}
  }
  return { type }
}

type OrderedListCommandProps = {
  editor: Editor
  commands: SingleCommands
  chain: () => ChainedCommands
}

export const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      type: {
        default: null,
        parseHTML: element => parseOrderedListType(element.getAttribute('type')),
        renderHTML: attributes => orderedListTypeForRender(attributes.type)
      }
    }
  },

  renderHTML({ HTMLAttributes }) {
    const { start, type, ...rest } = HTMLAttributes
    const typeAttrs = orderedListTypeForRender(type)
    const merged = mergeAttributes(this.options.HTMLAttributes, rest, typeAttrs)

    return start === 1 ? ['ol', merged, 0] : ['ol', mergeAttributes(merged, { start }), 0]
  },

  addCommands() {
    const itemTypeName = this.options.itemTypeName
    const keepMarks = this.options.keepMarks

    return {
      toggleOrderedList:
        () =>
        ({ editor, commands, chain }: OrderedListCommandProps) => {
          if (editor.isActive(this.name)) {
            const currentType = editor.getAttributes(this.name).type
            if (currentType) {
              return chain().updateAttributes(this.name, { type: null }).run()
            }
            return commands.toggleList(this.name, itemTypeName, keepMarks)
          }

          return commands.toggleList(this.name, itemTypeName, keepMarks, { type: null })
        },

      toggleLetteredList:
        () =>
        ({ editor, commands, chain }: OrderedListCommandProps) => {
          const letteredType: OrderedListMarkerType = 'a'

          if (editor.isActive(this.name, { type: letteredType })) {
            return commands.toggleList(this.name, itemTypeName, keepMarks)
          }

          if (editor.isActive(this.name)) {
            return chain().updateAttributes(this.name, { type: letteredType }).run()
          }

          return commands.toggleList(this.name, itemTypeName, keepMarks, { type: letteredType })
        }
    }
  }
})
